/**
 * Build and download an Excel workbook of subscription child resources.
 * One "All Resources" sheet plus a sheet per resource type (Domain, Droplet, etc.).
 */
import * as XLSX from 'xlsx'
import { formatDate } from './subscriptionUtils'

const safeSheetName = (name) => {
  const cleaned = String(name || 'Sheet')
    .replace(/[\\/?*[\]:]/g, ' ')
    .trim()
    .slice(0, 31)
  return cleaned || 'Sheet'
}

const cellDate = (value) => {
  if (!value) return ''
  const formatted = formatDate(value)
  return formatted === '-' ? '' : formatted
}

const commonRow = (r, meta = {}) => ({
  'Subscription #': meta.subscriptionNumber || '',
  'Subscription': meta.subscriptionName || '',
  'Provider': meta.provider || '',
  'Resource name': r.name || '',
  'Type': r.resourceType || '',
  'Status': r.status || '',
  'Environment': r.environment || '',
  'Domain name': r.domainName || '',
  'Registrar / Provider': r.registrar || '',
  'Auto renewal': r.autoRenewal ? 'Yes' : 'No',
  'Registration date': cellDate(r.registrationDate),
  'Start date': cellDate(r.startDate),
  'Expiry date': cellDate(r.expiryDate),
  'Renewal date': cellDate(r.renewalDate),
  'Decommission date': cellDate(r.decommissionDate),
  'Renewal cost': Number(r.renewalCost) || 0,
  'Monthly est. cost': Number(r.monthlyEstimatedCost) || 0,
  'Actual latest cost': Number(r.actualLatestCost) || 0,
  'Product / project': r.productOrProject || '',
  'Account / region': r.accountAdministrator || '',
  'Name servers': r.nameServers || '',
  'Purchased licences': Number(r.purchasedLicences) || 0,
  'Assigned licences': Number(r.assignedLicences) || 0,
  'Cost per licence': Number(r.costPerLicence) || 0,
  'Asset code': r.assetCode || '',
  'Rental start': cellDate(r.rentalStartDate),
  'Rental end': cellDate(r.rentalEndDate),
  'Return due': cellDate(r.returnDueDate),
  'Return status': r.returnStatus || '',
  'Security deposit': Number(r.securityDeposit) || 0,
  'Condition': r.currentCondition || '',
  'Remarks': r.remarks || '',
})

const typeSpecificExtras = {
  Domain: (r) => ({
    'Domain name': r.domainName || r.name || '',
    'Registrar': r.registrar || '',
    'Privacy / notes': r.remarks || '',
  }),
  Droplet: (r) => ({
    'Droplet name': r.name || '',
    'Region / account': r.accountAdministrator || '',
    'Config notes': r.remarks || '',
  }),
  Database: (r) => ({
    'Configuration name': r.name || '',
    'Provider': r.registrar || 'MongoDB',
    'Linked notes': r.remarks || '',
  }),
  'Rental asset': (r) => ({
    'Asset code': r.assetCode || '',
    'Return due': cellDate(r.returnDueDate),
    'Return status': r.returnStatus || '',
  }),
}

const slugify = (value) =>
  String(value || 'subscription')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60) || 'subscription'

/**
 * @param {object} options
 * @param {object} options.subscription - parent subscription (name, number, provider)
 * @param {array} options.resources - resource rows
 * @param {string} [options.resourceType] - optional filter e.g. 'Domain'
 * @returns {{ count: number, filename: string }}
 */
export function exportSubscriptionResourcesExcel({
  subscription = {},
  resources = [],
  resourceType = '',
} = {}) {
  const meta = {
    subscriptionNumber: subscription.subscriptionNumber || subscription.subscriptionId || '',
    subscriptionName: subscription.subscriptionName || '',
    provider: subscription.vendorName || subscription.provider || '',
  }

  let list = Array.isArray(resources) ? [...resources] : []
  if (resourceType) {
    list = list.filter((r) => String(r.resourceType || '') === resourceType)
  }

  if (list.length === 0) {
    throw new Error(resourceType
      ? `No ${resourceType} resources to export`
      : 'No resources to export')
  }

  const workbook = XLSX.utils.book_new()

  const allRows = list.map((r) => commonRow(r, meta))
  const allSheet = XLSX.utils.json_to_sheet(allRows)
  XLSX.utils.book_append_sheet(workbook, allSheet, safeSheetName('All Resources'))

  const byType = {}
  list.forEach((r) => {
    const type = r.resourceType || 'Other'
    if (!byType[type]) byType[type] = []
    byType[type].push(r)
  })

  Object.entries(byType).forEach(([type, rows]) => {
    // Skip duplicate sheet when filtered to a single type already covered by All
    if (Object.keys(byType).length === 1 && !resourceType) {
      // still add typed sheet for clarity when only one type exists
    }
    const extrasFn = typeSpecificExtras[type]
    const typedRows = rows.map((r) => {
      const base = commonRow(r, meta)
      return extrasFn ? { ...base, ...extrasFn(r) } : base
    })
    const sheet = XLSX.utils.json_to_sheet(typedRows)
    XLSX.utils.book_append_sheet(workbook, sheet, safeSheetName(type))
  })

  const typeSuffix = resourceType ? `_${slugify(resourceType)}` : ''
  const filename = `${slugify(meta.subscriptionName || meta.subscriptionNumber || 'subscription')}_resources${typeSuffix}_${new Date().toISOString().split('T')[0]}.xlsx`
  XLSX.writeFile(workbook, filename)
  return { count: list.length, filename }
}

export default exportSubscriptionResourcesExcel
